import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  AcknowledgeModal,
  ActionModal,
  FormField,
  Modal,
  Select,
  SelectOption,
  Alert,
  Flex,
  Typography
} from '@percent/lemonade'
import { UpdateTaskStatusModalProps } from './UpdateTaskStatusModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'
import { TaskStatus } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'

export function UpdateTaskStatusModal({ task, refresh, onClose }: Readonly<UpdateTaskStatusModalProps>) {
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const { grantProgramService } = useServices()
  const { t } = useTranslation()

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: 'todo', label: t('grants.programDetails.candidates.tasks.todo') },
      { value: 'done', label: t('grants.programDetails.candidates.tasks.done') },
      { value: 'cancelled', label: t('grants.programDetails.candidates.tasks.cancelled') }
    ],
    [t]
  )

  const [{ isPending }, { apiFunc: updateTaskStatus }] = useMutation(
    grantProgramService.updateTaskStatus,
    () => {
      refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const { values, setFieldValue, handleSubmit } = useFormik<{
    status: TaskStatus
  }>({
    initialValues: {
      status: task.status
    },
    validationSchema: () =>
      Yup.object().shape({
        status: Yup.mixed<TaskStatus>().required('Required')
      }),
    onSubmit: formValues => {
      updateTaskStatus({ taskId: task.id, payload: { status: formValues.status } })
    }
  })

  const handleClose = () => {
    onClose()
    setModalState(null)
  }

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programDetails.candidates.modal.updateTaskStatus.success.title')}
      description={t('grants.programDetails.candidates.modal.updateTaskStatus.success.description')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="update-task-status-success-modal"
      buttonTestId="close-update-task-status-success-modal"
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programDetails.candidates.modal.updateTaskStatus.error.title')}
      description={t('grants.programDetails.candidates.modal.updateTaskStatus.error.description')}
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="update-task-status-error-modal"
      buttonTestId="close-update-task-status-error-modal"
    />
  )

  return (
    <Modal open={!!task} onClose={onClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('grants.programDetails.candidates.tasks.updateTaskStatus.title')}
          loading={isPending}
          primaryButtonText={t('button.save')}
          primaryBtnTestId="update-task-status-modal-button"
          secondaryButtonText={t('button.cancel')}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
        >
          <Flex direction="column" gap="16px">
            <Typography variant="small">
              {t('grants.programDetails.candidates.tasks.updateStatus.description', { taskName: task.name })}
            </Typography>
            <Alert variant="info" title={t('grants.programDetails.candidates.modal.updateTaskStatus.info')} />

            <FormField
              label={t('grants.programDetails.candidates.tasks.status')}
              data-testid="task-status-input"
              necessity="required"
            >
              <Select
                name="status"
                placeholder={t('grants.programDetails.candidates.tasks.status')}
                options={statusOptions}
                defaultValue={statusOptions.find(option => option.value === values.status)}
                onChange={({ value }) => setFieldValue('status', value as TaskStatus)}
                data-testid="task-status-select"
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
